// @ts-nocheck
import { useState, useEffect, useRef } from 'react'
import { X, Save, AlertCircle, CheckCircle2, Volume2, Upload, Loader2, Music } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

export default function WelcomeMessageSettings({ phoneNumber, onClose, onSaved }) {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [activeTab, setActiveTab] = useState('incoming') // 'incoming' or 'waiting'
  const fileInputRef = useRef(null)

  // 着信メッセージ設定
  const [incomingConfig, setIncomingConfig] = useState({
    type: 'tts',
    ttsText: 'お電話ありがとうございます。ただいまオペレーターにおつなぎしますので、少々お待ちください。',
    ttsLanguage: 'ja-JP',
    ttsVoice: 'alice',
    audioUrl: '',
  })

  // 待機中メッセージ設定
  const [waitingConfig, setWaitingConfig] = useState({
    ttsText: 'お待たせしております。もうしばらくお待ちください。',
    ttsLanguage: 'ja-JP',
    ttsVoice: 'alice',
    audioUrl: '',
  })

  // 利用可能な音声オプション
  const voiceOptions = {
    'ja-JP': [
      { value: 'alice', label: 'Alice (女性)' },
      { value: 'Mizuki', label: 'Mizuki (女性)' },
      { value: 'Takumi', label: 'Takumi (男性)' },
    ],
    'en-US': [
      { value: 'alice', label: 'Alice (女性)' },
      { value: 'Polly.Joanna', label: 'Joanna (女性)' },
      { value: 'Polly.Matthew', label: 'Matthew (男性)' },
    ],
  }

  useEffect(() => {
    fetchSettings()
  }, [phoneNumber.phoneNumberId])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.get(`/api/phone-numbers/${phoneNumber.phoneNumberId}/welcome-message`)
      
      // 着信メッセージ設定
      if (data.welcomeMessageConfig) {
        setIncomingConfig(data.welcomeMessageConfig)
      }
      
      // 待機中メッセージ設定
      if (data.waitingMessageConfig) {
        setWaitingConfig(data.waitingMessageConfig)
      }
    } catch (err) {
      console.error('メッセージ設定取得エラー:', err)
      setError('設定の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      // バリデーション
      if (activeTab === 'incoming') {
        if (incomingConfig.type === 'tts' && !incomingConfig.ttsText?.trim()) {
          setError('メッセージテキストを入力してください')
          setSaving(false)
          return
        }

        if (incomingConfig.type === 'audio_url' && !incomingConfig.audioUrl?.trim()) {
          setError('録音ファイルをアップロードするか、URLを入力してください')
          setSaving(false)
          return
        }
      } else {
        if (!waitingConfig.ttsText?.trim() && !waitingConfig.audioUrl?.trim()) {
          setError('メッセージテキストまたは音声ファイルを設定してください')
          setSaving(false)
          return
        }
      }

      // 保存するデータを構築
      const payload = {
        welcomeMessageConfig: incomingConfig,
        waitingMessageConfig: waitingConfig,
      }

      await api.put(`/api/phone-numbers/${phoneNumber.phoneNumberId}/welcome-message`, payload)
      
      setSuccess(true)
      setTimeout(() => {
        onSaved?.()
        onClose()
      }, 1500)
    } catch (err) {
      console.error('メッセージ設定保存エラー:', err)
      setError(err.response?.data?.error || '設定の保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  // ファイルアップロード処理
  const handleFileUpload = async (file, targetConfig) => {
    if (!file) return

    // ファイルサイズチェック（10MB）
    if (file.size > 10 * 1024 * 1024) {
      setError('ファイルサイズは10MB以下にしてください')
      return
    }

    // ファイル形式チェック
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav']
    if (!allowedTypes.includes(file.type)) {
      setError('MP3またはWAV形式のファイルを選択してください')
      return
    }

    try {
      setUploading(true)
      setError(null)

      const formData = new FormData()
      formData.append('audio', file)

      const data = await api.uploadFile('/api/upload/audio', formData)
      
      if (targetConfig === 'incoming') {
        setIncomingConfig({ ...incomingConfig, audioUrl: data.url })
      } else {
        setWaitingConfig({ ...waitingConfig, audioUrl: data.url })
      }
      
      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (err) {
      console.error('ファイルアップロードエラー:', err)
      setError(err.message || 'ファイルのアップロードに失敗しました')
    } finally {
      setUploading(false)
    }
  }

  // ファイル選択イベント
  const handleFileSelect = (event, targetConfig) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileUpload(file, targetConfig)
    }
  }

  // ドラッグ&ドロップイベント
  const handleDragEnter = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = (event, targetConfig) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    
    const file = event.dataTransfer.files?.[0]
    if (file) {
      handleFileUpload(file, targetConfig)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">着信メッセージ設定</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {phoneNumber.name} ({formatPhoneNumberForDisplay(phoneNumber.phoneNumber)})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* タブ */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('incoming')}
            className={`px-4 py-2 font-medium text-sm transition-colors relative ${
              activeTab === 'incoming'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              着信メッセージ
            </div>
            {activeTab === 'incoming' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('waiting')}
            className={`px-4 py-2 font-medium text-sm transition-colors relative ${
              activeTab === 'waiting'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4" />
              待機中メッセージ
            </div>
            {activeTab === 'waiting' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
            )}
          </button>
        </div>

        {/* エラー・成功メッセージ */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-green-800 dark:text-green-200">設定を保存しました</span>
          </div>
        )}

        {/* 着信メッセージタブ */}
        {activeTab === 'incoming' && (
          <div className="space-y-6">
            {/* メッセージタイプ選択 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                メッセージタイプ
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setIncomingConfig({ ...incomingConfig, type: 'tts' })}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    incomingConfig.type === 'tts'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Volume2 className={`w-6 h-6 mx-auto mb-2 ${
                    incomingConfig.type === 'tts' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                  }`} />
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Text-to-Speech</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">テキストを音声に変換</div>
                </button>

                <button
                  type="button"
                  onClick={() => setIncomingConfig({ ...incomingConfig, type: 'audio_url' })}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    incomingConfig.type === 'audio_url'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${
                    incomingConfig.type === 'audio_url' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                  }`} />
                  <div className="text-sm font-medium text-gray-900 dark:text-white">録音ファイル</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">音声ファイルを使用</div>
                </button>
              </div>
            </div>

            {/* TTS設定 */}
            {incomingConfig.type === 'tts' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    メッセージテキスト <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={incomingConfig.ttsText || ''}
                    onChange={(e) => setIncomingConfig({ ...incomingConfig, ttsText: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="お電話ありがとうございます。ただいまオペレーターにおつなぎしますので、少々お待ちください。"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    着信時に再生されるメッセージを入力してください
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      言語
                    </label>
                    <select
                      value={incomingConfig.ttsLanguage || 'ja-JP'}
                      onChange={(e) => setIncomingConfig({ 
                        ...incomingConfig, 
                        ttsLanguage: e.target.value,
                        ttsVoice: voiceOptions[e.target.value]?.[0]?.value || 'alice'
                      })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="ja-JP">日本語</option>
                      <option value="en-US">英語 (米国)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      音声
                    </label>
                    <select
                      value={incomingConfig.ttsVoice || 'alice'}
                      onChange={(e) => setIncomingConfig({ ...incomingConfig, ttsVoice: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {(voiceOptions[incomingConfig.ttsLanguage || 'ja-JP'] || voiceOptions['ja-JP']).map((voice) => (
                        <option key={voice.value} value={voice.value}>
                          {voice.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* 録音ファイル設定 */}
            {incomingConfig.type === 'audio_url' && (
              <div className="space-y-4">
                {/* ファイルアップロード */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    音声ファイルをアップロード
                  </label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
                      onChange={(e) => handleFileSelect(e, 'incoming')}
                      disabled={uploading}
                      className="hidden"
                      id="incoming-audio-file-input"
                    />
                    <label
                      htmlFor="incoming-audio-file-input"
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, 'incoming')}
                      className={`flex-1 px-4 py-3 border-2 border-dashed rounded-lg text-center cursor-pointer transition-all ${
                        uploading
                          ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 cursor-not-allowed'
                          : isDragging
                          ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/20 scale-105'
                          : 'border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {uploading ? (
                          <>
                            <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">アップロード中...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              ファイルを選択またはドラッグ&ドロップ
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        MP3またはWAV形式（最大10MB）
                      </p>
                    </label>
                  </div>
                </div>

                {/* 区切り線 */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">または</span>
                  </div>
                </div>

                {/* URL直接入力 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    録音ファイルURL
                  </label>
                  <input
                    type="url"
                    value={incomingConfig.audioUrl || ''}
                    onChange={(e) => setIncomingConfig({ ...incomingConfig, audioUrl: e.target.value })}
                    disabled={uploading}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                    placeholder="https://example.com/welcome.mp3"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    既にホスティングされている音声ファイルのURLを直接入力
                  </p>
                </div>

                {/* 現在のファイル表示 */}
                {incomingConfig.audioUrl && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">ファイルが設定されています</p>
                        <p className="text-xs text-green-700 dark:text-green-300 mt-1 break-all">{incomingConfig.audioUrl}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 待機中メッセージタブ */}
        {activeTab === 'waiting' && (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                オペレーターが対応可能になるまでの待機中に再生されるメッセージと音楽を設定します。
              </p>
            </div>

            {/* TTSメッセージ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                待機メッセージ（Text-to-Speech）
              </label>
              <textarea
                value={waitingConfig.ttsText || ''}
                onChange={(e) => setWaitingConfig({ ...waitingConfig, ttsText: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="お待たせしております。もうしばらくお待ちください。"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                待機中に最初に再生されるメッセージ（空欄の場合は音楽のみ再生）
              </p>
            </div>

            {/* TTS設定 */}
            {waitingConfig.ttsText && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    言語
                  </label>
                  <select
                    value={waitingConfig.ttsLanguage || 'ja-JP'}
                    onChange={(e) => setWaitingConfig({ 
                      ...waitingConfig, 
                      ttsLanguage: e.target.value,
                      ttsVoice: voiceOptions[e.target.value]?.[0]?.value || 'alice'
                    })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="ja-JP">日本語</option>
                    <option value="en-US">英語 (米国)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    音声
                  </label>
                  <select
                    value={waitingConfig.ttsVoice || 'alice'}
                    onChange={(e) => setWaitingConfig({ ...waitingConfig, ttsVoice: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {(voiceOptions[waitingConfig.ttsLanguage || 'ja-JP'] || voiceOptions['ja-JP']).map((voice) => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* 待機音楽ファイル */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  待機音楽ファイル
                </label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
                    onChange={(e) => handleFileSelect(e, 'waiting')}
                    disabled={uploading}
                    className="hidden"
                    id="waiting-audio-file-input"
                  />
                  <label
                    htmlFor="waiting-audio-file-input"
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'waiting')}
                    className={`flex-1 px-4 py-3 border-2 border-dashed rounded-lg text-center cursor-pointer transition-all ${
                      uploading
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 cursor-not-allowed'
                        : isDragging
                        ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/20 scale-105'
                        : 'border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {uploading ? (
                        <>
                          <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">アップロード中...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            ファイルを選択またはドラッグ&ドロップ
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      MP3またはWAV形式（最大10MB）
                    </p>
                  </label>
                </div>
              </div>

              {/* 区切り線 */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">または</span>
                </div>
              </div>

              {/* URL直接入力 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  音楽ファイルURL
                </label>
                <input
                  type="url"
                  value={waitingConfig.audioUrl || ''}
                  onChange={(e) => setWaitingConfig({ ...waitingConfig, audioUrl: e.target.value })}
                  disabled={uploading}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                  placeholder="https://example.com/waiting-music.mp3"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  既にホスティングされている音楽ファイルのURLを直接入力
                </p>
              </div>

              {/* 現在のファイル表示 */}
              {waitingConfig.audioUrl && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">ファイルが設定されています</p>
                      <p className="text-xs text-green-700 dark:text-green-300 mt-1 break-all">{waitingConfig.audioUrl}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                保存中...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                保存
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
